
  import { ref, toRefs, defineComponent, onMounted, nextTick, computed } from 'vue'
  import { OriginatorsPublicApi } from "@/core/services/apis"
  import { DotsHorizontalIcon } from '@heroicons/vue/solid'
  import { PostHogTracker } from '@/core/services/trackers'
  import { useRoute } from 'vue-router'


  export default defineComponent({
    name: 'Chat',
    
    props: ['originator', 'botName'],

    emits: ['ended'],

    components: {
      DotsHorizontalIcon
    },

    setup(props, { emit }) {
      const messages = ref<any>([])
      const newMessage = ref('')
      const scrollable = ref<any>(null)
      const conversationId = ref<any>(null)
      const loading = ref<boolean>(false)
      const { originator } = toRefs<any>(props)
      const isDone = ref<boolean>(false)
      const isFocused = ref<boolean>(false)
      const route = useRoute()

      const clientId = computed(() => {
        if (route.name == 'GroundbreakerChat') {
          return 'groundbreaker'
        }
        return route.name == 'LendingOneChat' ? 'lendingone' : 'gelt'
      })

      const initialMessage = computed(() => {
        if (route.name == 'GroundbreakerChat') {
          return '👋 Welcome to Groundbreaker, would you like to impress your investors and close more deals?';
        }
        return '👋 Hi there! Can I help you get started with a new loan quote? Or do you have any questions I can answer first?';
      });

      const formatTime = (timestamp) => {
        const d = new Date(timestamp)

        return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
      }

      const scrollToBottom = () => {
        nextTick(() => {
          if (scrollable.value) {
            scrollable.value.scrollTop = scrollable.value.scrollHeight
          }
        })
      }

      const sendMessage = () => {
        if (newMessage.value.trim() !== '') {
          loading.value = true
          PostHogTracker.capture('Message Sent', {
            message: newMessage.value,
            originatorId: originator?.value?._id || '',
            clientId: clientId.value
          });
          messages.value.push({ sender: 'user', text: newMessage.value, timestamp: new Date() })
          socket.value.send(JSON.stringify({
            conversationId: conversationId.value,
            userMessage: newMessage.value,
            originator: originator?.value?._id || '',
            clientId: clientId.value
          }))
          newMessage.value = ''
          scrollToBottom()
        }
      }

      const isPreviousMessageFromSameSender = (index: number) => {
        return index > 0 && messages.value[index - 1].sender === messages.value[index].sender;
      }

      const socket = ref<any>(null)

      onMounted(async () => {
        conversationId.value = localStorage.getItem('ALxWLyRvbmgSeStS')

        if (conversationId.value) {
          try {
            const { data } = await OriginatorsPublicApi.getConversation(conversationId.value)
            messages.value = data.messages
            isDone.value = data.should_terminate_chat
          } catch (error) {
            localStorage.removeItem('ALxWLyRvbmgSeStS')
          }
        } else {
          newMessage.value = ''
          messages.value = [
            {
              sender: 'system',
              text: initialMessage.value,
              timestamp: new Date()
            }
          ]
          nextTick(() => {
            document?.getElementById('chat-message')?.focus()
          })
        }

        socket.value = new WebSocket(process.env.VUE_APP_NODE_WSS_URL)

        socket.value.onopen = () => {
          console.log('Conversation started');
        }

        socket.value.onmessage = (event) => {
          const response = JSON.parse(event.data)
          conversationId.value = response._id
          localStorage.setItem('ALxWLyRvbmgSeStS', conversationId.value)
          messages.value = []
          messages.value = response.messages
          isDone.value = response.should_terminate_chat
          scrollToBottom()
          loading.value = false
        }

        scrollToBottom()
      })

      const endChat = () => {
        emit('ended')
      }

      const startNewConversation = () => {
        newMessage.value = ''
        messages.value = []
        conversationId.value = null
        localStorage.removeItem('ALxWLyRvbmgSeStS')
        loading.value = false
        isDone.value = false
      }

      return {
        messages,
        newMessage,
        sendMessage,
        scrollable,
        formatTime,
        loading,
        isDone,
        endChat,
        startNewConversation,
        isPreviousMessageFromSameSender,
        isFocused
      }
    },
  })
